// Single source of truth for the portfolio's URL + the parent brand
// site URL. Importing these constants instead of duplicating the env-var
// fallback inline avoids drift between files (the SEO audit caught a
// case where the default `'https://wantek.pt'` collapsed the portfolio
// `@id` and the parent `@id` onto the same value, breaking the JSON-LD
// `isPartOf` hierarchy).
//
// PARENT_SITE is a hard constant — it anchors the Organization `@id`
// shared with the parent brand and must never vary by environment.
//
// SITE_URL is this portfolio's own URL. In production set
// NEXT_PUBLIC_SITE_URL to the subdomain (e.g. `https://portfolio.wantek.pt`)
// so the WebSite `@id` is distinct from the parent's. The fallback
// matches the documented deploy target in `.env.template` so a local
// dev session doesn't accidentally collide with the parent domain.
export const PARENT_SITE = 'https://wantek.pt';

export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL ?? 'https://portfolio.wantek.pt';

/**
 * Canonical contact URL on the parent brand site. Every CTA in the
 * portfolio (header, InfoSection chapter 04, mobile contact button)
 * points here instead of opening a mailto on the visitor's device —
 * routes the lead through Wantek's own contact funnel where it can be
 * tracked, qualified and follow-up'd, rather than vanishing into a
 * desktop email client. The bare mailto is preserved only in the
 * footer (where the email is shown as literal text, not as a CTA).
 */
export const PARENT_CONTACT_URL = `${PARENT_SITE}/contact`;

/**
 * Wantek's canonical social profiles. Used in:
 *   • `Organization.sameAs` — schema.org's standard way of telling
 *     crawlers "this entity also exists at these URLs". Strengthens
 *     the Knowledge Graph match between the brand and its profiles.
 *   • `<link rel="me">` in the head — identity-verification convention
 *     read by some crawlers and the Mastodon/IndieWeb stack to confirm
 *     cross-account ownership.
 *
 * Add new profiles here, then they get picked up by both surfaces.
 */
export const SOCIAL_URLS = {
  instagram: 'https://www.instagram.com/wantek.pt/',
} as const;

/**
 * The last time the portfolio's content (projects, copy, messages) changed
 * in a way crawlers should know about. Manually bumped on each
 * content-affecting deploy — avoids the `new Date()` antipattern that would
 * report "modified just now" on every build and confuse freshness signals.
 *
 * Shared between:
 *   • `app/sitemap.ts` (lastModified on each URL)
 *   • `app/[locale]/layout.tsx` (WebSite JSON-LD `dateModified`)
 *   • `app/[locale]/grid/page.tsx` (ItemList JSON-LD `dateModified`)
 */
export const CONTENT_LAST_MODIFIED = new Date('2026-06-25');
